import { Button, Flex, Form, Radio, Select } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'translations/hooks';

import { BoardViewMode } from 'ui/quality-control/board/utils';

import { DefectStatus, ReportState } from './types';

export const BoardActions = ({
  openComplianceModal,
  defectFilter,
  onDefectFilterChange,
  defectReportStateFilter,
  onDefectReportStateFilterChange,
  repairReportStateFilter,
  onRepairReportStateFilterChange,
  boardViewMode,
  onBoardViewModeChange,
  canManageCompliance,
}: {
  openComplianceModal: () => void;
  defectFilter: DefectStatus;
  onDefectFilterChange: (value: DefectStatus) => void;
  defectReportStateFilter: ReportState;
  onDefectReportStateFilterChange: (value: ReportState) => void;
  repairReportStateFilter: ReportState;
  onRepairReportStateFilterChange: (value: ReportState) => void;
  boardViewMode: BoardViewMode;
  onBoardViewModeChange: (value: BoardViewMode) => void;
  canManageCompliance: boolean;
}) => {
  const { t } = useTranslation();

  const defectStatusOptions = useMemo(() => {
    return Object.values(DefectStatus).map((range) => ({
      label: t(
        'qualityControl.recordSegmentDefect.leftPanel.defectStatus.options.option',
        {
          context: range,
        },
      ),
      value: range,
    }));
  }, [t]);

  const defectReportStateOptions = useMemo(() => {
    return Object.values(ReportState).map((state) => ({
      label: t(
        'qualityControl.recordSegmentDefect.leftPanel.defectReportState.options.option',
        {
          context: state,
        },
      ),
      value: state,
    }));
  }, [t]);

  const repairReportStateOptions = useMemo(() => {
    return Object.values(ReportState).map((state) => ({
      label: t(
        'qualityControl.recordSegmentDefect.leftPanel.repairReportState.options.option',
        {
          context: state,
        },
      ),
      value: state,
    }));
  }, [t]);

  const boardViewModeOptions = useMemo(() => {
    return Object.values(BoardViewMode).map((mode) => ({
      label: t(
        'qualityControl.recordSegmentDefect.leftPanel.boardViewMode.options.option',
        {
          context: mode,
        },
      ),
      value: mode,
    }));
  }, [t]);

  return (
    <Flex
      justify="space-between"
      align="center"
      style={{
        marginBottom: '24px',
      }}
    >
      <Form layout="inline">
        <Form.Item
          label={t(
            'qualityControl.recordSegmentDefect.leftPanel.boardViewMode.label',
          )}
          layout="vertical"
        >
          <Radio.Group
            options={boardViewModeOptions}
            value={boardViewMode}
            onChange={(e) => onBoardViewModeChange(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          />
        </Form.Item>
        <Form.Item
          label={t(
            'qualityControl.recordSegmentDefect.leftPanel.defectStatus.label',
          )}
          layout="vertical"
        >
          <Select
            placeholder={t('input.segmentTypes.placeholder')}
            options={defectStatusOptions}
            value={defectFilter}
            onChange={onDefectFilterChange}
          />
        </Form.Item>
        <Form.Item
          label={t(
            'qualityControl.recordSegmentDefect.leftPanel.defectReportState.label',
          )}
          layout="vertical"
        >
          <Select
            placeholder={t('input.segmentTypes.placeholder')}
            options={defectReportStateOptions}
            value={defectReportStateFilter}
            onChange={onDefectReportStateFilterChange}
          />
        </Form.Item>
        <Form.Item
          label={t(
            'qualityControl.recordSegmentDefect.leftPanel.repairReportState.label',
          )}
          layout="vertical"
        >
          <Select
            placeholder={t('input.segmentTypes.placeholder')}
            options={repairReportStateOptions}
            value={repairReportStateFilter}
            onChange={onRepairReportStateFilterChange}
          />
        </Form.Item>
      </Form>
      {canManageCompliance ? (
        <Button onClick={openComplianceModal}>
          {t('qualityControl.recordSegmentDefect.complianceModal.title')}
        </Button>
      ) : null}
    </Flex>
  );
};
